import React, { useState, useMemo, useCallback } from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  displayPrice,
  isPriceVariationsEnabled,
  requireListingImage,
} from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
} from '../../components';
import { FaMapPin, FaToilet, FaBed, FaArrowAltCircleRight, FaStar } from 'react-icons/fa';
import { MdPeopleAlt } from 'react-icons/md';
import { IconRule } from '../../components';

import { types as sdkTypes } from '../../util/sdkLoader';

const { Money } = sdkTypes;

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;
const FADE_MS = 280;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl, listingTypeConfig } = props;
  const showPrice = displayPrice(listingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);

  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? (
    <span className={css.perUnit}>
      <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
    </span>
  ) : (
    ''
  );

  return (
    <div className={css.price} title={priceTitle}>
      {hasMultiplePriceVariants ? (
        <FormattedMessage
          id="ListingCard.priceStartingFrom"
          values={{ priceValue, pricePerUnit }}
        />
      ) : (
        <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit }} />
      )}
    </div>
  );
};

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.currentListing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    currentListing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    isSearchCard,
    variantPrefix,
    showListingImage,
    style,
  } = props;
  
  const images = currentListing.images || [];
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFadingOut, setIsFadingOut] = useState(false);

    React.useEffect(() => {
    setActiveIndex(0);
  }, [currentListing?.id?.uuid]);

  const hasImages = images.length > 0;
  const showCarousel = showListingImage && images.length > 1;

  const activeImage = hasImages ? images[activeIndex] : null;

  const variants = useMemo(() => {
    const v = activeImage?.attributes?.variants;
    return v ? Object.keys(v) : [];
  }, [activeImage]);

const changeImage = useCallback(
  (e, newIndex) => {
    e.preventDefault();
    e.stopPropagation();

    if (newIndex === activeIndex) return;

    setIsFadingOut(true);

    window.setTimeout(() => {
      setActiveIndex(newIndex);
      setIsFadingOut(false);
    }, FADE_MS);
  },
  [activeIndex]
);

const goPrev = useCallback(
  e => {
    const newIndex = (activeIndex - 1 + images.length) % images.length;
    changeImage(e, newIndex);
  },
  [activeIndex, images.length, changeImage]
);

const goNext = useCallback(
  e => {
    const newIndex = (activeIndex + 1) % images.length;
    changeImage(e, newIndex);
  },
  [activeIndex, images.length, changeImage]
);

  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  // Render the listing image only if listing images are enabled in the listing type
  return showListingImage ? (
    <AspectRatioWrapper
      className={isSearchCard ? css.aspectRatioWrapper : css.sectionListingCard}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <div className={css.carouselRoot}>
       <div className={classNames(css.imageWrapper, { [css.fadeOut]: isFadingOut })}>
      <LazyImage
        key={activeImage?.id?.uuid || activeIndex}
        rootClassName={css.rootForImage}
        alt={title}
        image={activeImage}
        variants={variants}
        sizes={renderSizes}
      />
      </div>
        {showCarousel ? (
          <>
            <button
              type="button"
              className={`${css.carouselArrow} ${css.carouselArrowLeft}`}
              onClick={goPrev}
              aria-label="Previous image"
            >
              ‹
            </button>

            <button
              type="button"
              className={`${css.carouselArrow} ${css.carouselArrowRight}`}
              onClick={goNext}
              aria-label="Next image"
            >
              ›
            </button>

            <div className={css.carouselDots} onClick={e => changeImage(e, idx)}>
              {images.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={idx === activeIndex ? css.dotActive : css.dot}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setActiveIndex(idx);
                  }}
                  aria-label={`Go to image ${idx + 1}`}
                />
              ))}
            </div>
          </>
        ) : null}
        </div>
      
    </AspectRatioWrapper>
  ) : (
    <ListingCardThumbnail
      style={style}
      listingTitle={title}
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      setActivePropsMaybe={setActivePropsMaybe}
    />
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    showViewDetails = false,
    isSearchCard = true,
    isSearchPage = false,
    setActiveListing,
    showAuthorInfo = true,
    aspectWidth: aspectWidthProp,
    aspectHeight: aspectHeightProp,
  } = props;

  const { avgRating, reviewCount } = listing.attributes;

  const classes = classNames(rootClassName || css.root, className);

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);

  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const guest = publicData?.guest_capacity;
  const bathrooms = publicData?.number_bathrooms / 2;
  const beds = publicData?.number_beds;
  const surface = publicData?.surface / 100;
  const fullAddress = publicData?.location?.address;
  const city = publicData?.location?.city;
  const state = publicData?.location?.state;
  const shortLocation = [city, state].filter(Boolean).join(', ');
  const location = fullAddress
    ? fullAddress.split(',').slice(-2).map(s => s.trim()).join(', ')
    : null;

  const monthlyPrice = publicData?.priceSettings?.monthlyPrice;
  const weeklyPrice = publicData?.priceSettings?.weeklyPrice;
  const dailyPrice = publicData?.priceSettings?.price;

  const selectedPrice = monthlyPrice
    ? monthlyPrice
    : weeklyPrice
    ? weeklyPrice
    : dailyPrice
    ? dailyPrice
    : null;

  const priceMoney = selectedPrice
    ? new Money(selectedPrice?.amount, selectedPrice?.currency)
    : null;
  const formattedPrice = selectedPrice ? formatMoney(intl, priceMoney) : null;

  const selectedPlan = monthlyPrice ? '/month' : weeklyPrice ? '/week' : dailyPrice ? '/day' : null;

  const { listingType, cardStyle } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);
  const showBathrooms = bathrooms;
  const showLocation = shortLocation.length > 0;

  // const reviewLength = reviews.length;
  // const avgReviews =
  //   reviewLength > 0
  //     ? reviews.reduce((sum, r) => sum + (r.attributes.rating || 0), 0) / reviews.length
  //     : 0;

  // const reviewRating = avgReviews.toFixed(2);

  const {
    aspectWidth: configAspectWidth = 1,
    aspectHeight: configAspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  const aspectWidth = aspectWidthProp || configAspectWidth;
  const aspectHeight = aspectHeightProp || configAspectHeight;

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
      <ListingCardImage
        renderSizes={renderSizes}
        title={title}
        currentListing={currentListing}
        config={config}
        isSearchCard={isSearchCard}
        setActivePropsMaybe={setActivePropsMaybe}
        aspectWidth={aspectWidth}
        aspectHeight={aspectHeight}
        variantPrefix={variantPrefix}
        style={cardStyle}
        showListingImage={showListingImage}
      />
      <div className={css.info}>
        <div className={css.mainInfo}>
          <div className={css.textContainer}>
          {showListingImage && (
            <div className={css.titleRow}>
              <div className={`${css.title} ${!isSearchPage && css.compact}`}>
                {richText(title, {
                  longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                  longWordClass: css.longWord,
                })}
              </div>
              {isSearchPage ?
              <div className={css.rating}>
                <FaStar className={css.starIcon} />
                <span className={css.ratingValue}>{avgRating}</span>
                <span className={css.ratingCount}>({reviewCount})</span>
                </div> : null}
            </div>
          )}

          {showLocation ? (
            <div className={css.locationContainer}>
              <FaMapPin className={css.pinIcon} />
              <p className={`${css.listingText} ${css.locationText}`}>{shortLocation}</p>
            </div>
          ) : null}

          <div className={css.attributesContainer}>
            {guest ? (
              <div className={css.attribute}>
                <MdPeopleAlt className={css.icon} />
                <span>{guest}</span>
              </div>
            ) : null}

            {bathrooms ? (
              <div className={css.attribute}>
                <FaToilet className={css.icon} />
                <span>{bathrooms}</span>
              </div>
            ) : null}

            {beds ? (
              <div className={css.attribute}>
                <FaBed className={css.icon} />
                <span>{beds}</span>
              </div>
            ) : null}

            {surface ? (
              <div className={css.attribute}>
                <IconRule className={css.icon} />
                <span>{surface} m²</span>
              </div>
            ) : null}
          </div>
          </div>

          {selectedPrice ? (
            <div className={css.pricePlan}>
              <span className={css.selectedPrice}>{formattedPrice}</span>
              <span className={css.selectedPlan}>{selectedPlan}</span>
            </div>
          ) : null}
        </div>
        {showViewDetails ? (
          <div className={`${css.viewDetails} ${!showViewDetails && css.invisble}`}>
            <span>View details</span>
            <FaArrowAltCircleRight />
          </div>
        ) : null}
      </div>
    </NamedLink>
  );
};

export default ListingCard;
